import React, { useCallback, useEffect, useRef, useState } from "react"

import { Checkbox, Dropdown, Flex, Typography } from "antd"
import { useTranslation } from "react-i18next"

import Icon from "../../Icon"
import { alertsColumnIconMap } from "../../Utils/utils"
import Button from "../Button"
import InputComponent from "../Input"
import { defaultFiltersValue } from "./defaultFiltersValue"
import DropdownBlockCheckbox from "./DropdownBlockCheckbox"
import DropdownBlockEnrollments from "./DropdownBlockEnrollments"
import DropdownBlockNumber from "./DropdownBlockNumber"
import DropdownBlockTags from "./DropdownBlockTags"
import DropdownDateBlock from "./DropdownDateBlock/DropdownDateBlock"
import { defaultOptions } from "./filterOptionsData"
import { filtersTypeById, filtersTypeByIdMap } from "./filtersTypeById"

import "./styles.less"

const { Text } = Typography
const Filters = ({
  visibleFilters,
  filterOptions,
  allFilters,
  filters,
  onChangeFilter,
  filtersType,
  withoutAdd,
  showClearBtn,
  onClearFilters,
  newTagStyle,
  addGlobalFilter,
  removeGlobalFilter,
  columnIconMap,
  isMobile = false
}) => {
  const { t } = useTranslation()
  const filtersRef = useRef(null)

  const [showScrollButtons, setShowScrollButtons] = useState(false)
  const [canScrollLeft, setCanScrollLeft] = useState(false)

  const [openFilters, setOpenFilters] = useState(false)
  const [searchFilters, setSearchFilters] = useState("") //search for add filter dropdown

  const anyDropdownOpen = useRef(false)
  const handleFilterOpenChange = useCallback((isOpen) => {
    anyDropdownOpen.current = isOpen
  }, [])

  const visibleKeysList = visibleFilters
    ? Object.keys(visibleFilters)?.filter((key) => visibleFilters[key])
    : []

  const filtersTypeByIdFinal = filtersTypeByIdMap?.[filtersType] || filtersTypeById

  const finalColumnIconMap = columnIconMap || alertsColumnIconMap

  const checkOverflow = () => {
    const element = filtersRef.current
    const hasOverflow = element.scrollWidth > element.clientWidth
    setShowScrollButtons(hasOverflow)
  }

  const checkScrollPosition = () => {
    const element = filtersRef.current
    setCanScrollLeft(element.scrollLeft > 0)
  }

  const mobileScroll = (e) => {
    if (!isMobile) return
    if (anyDropdownOpen.current) {
      e.preventDefault()
    }
  }

  useEffect(() => {
    const element = filtersRef.current
    checkOverflow()
    checkScrollPosition()
    element.addEventListener("scroll", checkScrollPosition)
    window.addEventListener("resize", checkOverflow)
    element.addEventListener("touchmove", mobileScroll)
    return () => {
      element.removeEventListener("scroll", checkScrollPosition)
      window.removeEventListener("resize", checkOverflow)
      element.removeEventListener("touchmove", mobileScroll)
    }
  }, [])

  const setScrollToEnd = () => {
    const element = filtersRef.current
    element.scrollBy({ left: 2000 })
  }

  const handleChangeVisibleFilter = (e, key) => {
    if (visibleFilters?.[key]) {
      onChangeFilter({ [key]: false }, "changeVisible")
      if (filters[key] !== "all" && filters[key] !== null && filters[key]?.type !== null) {
        onChangeFilter({
          ...filters,
          [key]: defaultFiltersValue?.[filtersTypeByIdFinal?.[key]?.type]
        })
      }
    } else {
      if (filtersTypeByIdFinal[key]) {
        onChangeFilter({ [key]: true }, "changeVisible")
      }
    }
    setTimeout(() => checkOverflow(), 200)
    setTimeout(() => setScrollToEnd(), 50)
  }

  const handleOpenChange = (nextOpen, info) => {
    if (info.source === "trigger" || nextOpen) {
      anyDropdownOpen.current = nextOpen
      setOpenFilters(nextOpen)
      if (!nextOpen) {
        setSearchFilters("")
      }
    }
  }

  const handleScroll = () => {
    if (filtersRef.current) {
      if (canScrollLeft) {
        filtersRef.current.scrollBy({ left: -2000, behavior: "smooth" })
      } else {
        filtersRef.current.scrollBy({ left: 2000, behavior: "smooth" })
      }
    }
  }

  const scrollFilterToCenter = (e) => {
    if (!isMobile) return
    e.currentTarget.scrollIntoView({
      behavior: "smooth",
      inline: "nearest",
      block: "nearest"
    })
  }

  const handleChangeHide = (key, newValue) => {
    if (newValue) {
      onChangeFilter({
        exclude_fields: [...(filters?.exclude_fields || []), key]
      })
    } else {
      onChangeFilter({
        exclude_fields: [...filters?.exclude_fields?.filter((el) => el !== key)]
      })
    }
  }

  return (
    <Flex align="center" className="alert-filters-container">
      <Flex ref={filtersRef} gap={12} align="center" className="alert-filters">
        {visibleKeysList.map((key) => {
          let filterProp = filtersTypeByIdFinal[key]
          let dropdown
          switch (filterProp?.type) {
            case "checkbox": {
              dropdown = (
                <DropdownBlockCheckbox
                  key={key}
                  {...(!isMobile && { iconType: finalColumnIconMap[key] })}
                  newTagStyle={newTagStyle}
                  dropdownFilters={filters[key]}
                  setDropdownFilters={onChangeFilter}
                  data={defaultOptions[key] || filterOptions[key]}
                  onOpenChange={handleFilterOpenChange}
                  {...filterProp}
                />
              )
              break
            }
            case "enrollments": {
              dropdown = (
                <DropdownBlockEnrollments
                  key={key}
                  {...(!isMobile && { iconType: finalColumnIconMap[key] })}
                  dropdownFilters={filters[key]}
                  setDropdownFilters={onChangeFilter}
                  data={defaultOptions[key] || filterOptions[key]}
                  onOpenChange={handleFilterOpenChange}
                  {...filterProp}
                />
              )
              break
            }
            case "tags": {
              dropdown = (
                <DropdownBlockTags
                  key={key}
                  {...(!isMobile && { iconType: finalColumnIconMap[key] })}
                  dropdownFilters={filters[key]}
                  setDropdownFilters={onChangeFilter}
                  valueHide={filters?.["exclude_fields"]?.includes(key)}
                  onChangeHide={handleChangeHide}
                  addGlobalFilter={addGlobalFilter}
                  removeGlobalFilter={removeGlobalFilter}
                  onOpenChange={handleFilterOpenChange}
                  {...filterProp}
                />
              )
              break
            }
            case "number": {
              dropdown = (
                <DropdownBlockNumber
                  key={key}
                  {...(!isMobile && { iconType: finalColumnIconMap[key] })}
                  dropdownFilters={filters[key]}
                  setDropdownFilters={onChangeFilter}
                  onOpenChange={handleFilterOpenChange}
                  {...filterProp}
                />
              )
              break
            }
            case "whole_number": {
              dropdown = (
                <DropdownBlockNumber
                  key={key}
                  {...(!isMobile && { iconType: finalColumnIconMap[key] })}
                  dropdownFilters={filters[key]}
                  setDropdownFilters={onChangeFilter}
                  onOpenChange={handleFilterOpenChange}
                  {...filterProp}
                  whole_number
                />
              )
              break
            }
            case "date": {
              dropdown = (
                <DropdownDateBlock
                  key={key}
                  id={key}
                  {...(!isMobile && { iconType: finalColumnIconMap["transaction_timestamp"] })}
                  dropdownFilters={filters[key]}
                  setDropdownFilters={onChangeFilter}
                  onOpenChange={handleFilterOpenChange}
                  {...filterProp}
                />
              )
              break
            }
          }
          return dropdown ? (
            <div key={key} onClick={scrollFilterToCenter}>
              {dropdown}
            </div>
          ) : null
        })}

        {withoutAdd ? null : (
          <Dropdown
            // overlayClassName="custom-menu"
            getPopupContainer={() => document.getElementById("main-layout")}
            overlayStyle={{ width: 268 }}
            menu={{
              items: [
                ...(allFilters
                  ?.filter((el) => filtersTypeByIdFinal?.[el?.field_id])
                  ?.filter((filter) => {
                    const searchLower = searchFilters?.toLowerCase()
                    if (!searchLower) return true
                    return (
                      filter?.label?.toLowerCase()?.includes(searchLower) ||
                      filter?.field_id?.toLowerCase()?.includes(searchLower)
                    )
                  })
                  ?.map((filter) => ({
                    key: filter?.field_id,
                    label: (
                      <Flex align="center" gap={8}>
                        <Checkbox
                          checked={visibleFilters?.[filter?.field_id]}
                          onChange={(e) => handleChangeVisibleFilter(e, filter?.field_id)}
                        ></Checkbox>
                        {!isMobile && finalColumnIconMap?.[filter?.field_id] && (
                          <Icon
                            type={finalColumnIconMap?.[filter?.field_id]}
                            style={{ fontSize: 20, lineHeight: 1, margin: 0 }}
                          />
                        )}
                        <Text>{filter?.label}</Text>
                      </Flex>
                    )
                  })) ?? [])
              ],
              onClick: ({ key, domEvent }) => handleChangeVisibleFilter(domEvent, key)
            }}
            trigger={["click"]}
            onOpenChange={handleOpenChange}
            open={openFilters}
            placement={isMobile ? "bottom" : "bottomLeft"}
            dropdownRender={(menu) => (
              <div className="dropdown-database-menu add-filters">
                <div className="dropdown-search-wrapper">
                  <InputComponent
                    className="dropdown-search"
                    prefix={<Icon type="search" role="icon" />}
                    placeholder={`${t("Search")}...`}
                    value={searchFilters}
                    onChange={(e) => setSearchFilters(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                {menu}
              </div>
            )}
          >
            <div
              className="edit-columns-trigger"
              onClick={(e) => {
                e.stopPropagation()
                scrollFilterToCenter(e)
              }}
            >
              <Button
                type="text"
                className="flex center"
                icon={<Icon type="plus" style={{ fontSize: 20 }} />}
              >
                {t("Add filter")}
              </Button>
            </div>
          </Dropdown>
        )}

        {showClearBtn && (
          <Button className="flex" onClick={onClearFilters}>
            {t("Reset all")}
          </Button>
        )}
      </Flex>

      {showScrollButtons && !isMobile && (
        <Button
          icon={<Icon type={canScrollLeft ? "chevron_left_small" : "chevron_right_small"} />}
          onClick={handleScroll}
          className={"flex center scroll-btn-right"}
        />
      )}
    </Flex>
  )
}

export default React.memo(Filters)
